<template>
  <div class="documents-container">
    <!-- Legal Links Configuration -->
    <div class="config-section">
      <h3 class="section-title">Legal Links</h3>
      <div class="config-card">
        <div class="config-row">
          <div v-for="which in ['privacy', 'terms']" :key="which" class="config-item">
            <label class="config-label">{{ which === 'privacy' ? 'Privacy Policy' : 'Terms of Service' }}</label>
            <div class="type-toggles">
              <label
                class="type-chip"
                :class="{ active: legalConfig[which].type === 'none' }"
                @click="setLegalType(which, 'none')"
              >None</label>
              <label
                class="type-chip"
                :class="{ active: legalConfig[which].type === 'document' }"
                @click="setLegalType(which, 'document')"
              >Document</label>
              <label
                class="type-chip"
                :class="{ active: legalConfig[which].type === 'link' }"
                @click="setLegalType(which, 'link')"
              >External link</label>
            </div>
            <select
              v-if="legalConfig[which].type === 'document'"
              v-model="legalConfig[which].slug"
              class="legal-select"
              @change="saveLegalConfig"
            >
              <option disabled value="">Select a document...</option>
              <option v-for="doc in items" :key="doc.id" :value="doc.slug">
                {{ doc.title }} (/docs/{{ doc.slug }}){{ doc.is_active ? '' : ' — inactive!' }}
              </option>
            </select>
            <input
              v-if="legalConfig[which].type === 'link'"
              v-model="legalConfig[which].url"
              type="text"
              placeholder="https://..."
              class="legal-url-input"
              @change="saveLegalConfig"
            />
            <p class="config-hint">
              {{ which === 'privacy' ? 'Shown as the privacy policy entry in the app menu' : 'Shown as the terms of service entry in the app menu' }}
            </p>
          </div>
        </div>
        <transition name="fade">
          <div v-if="configSaving" class="config-status saving">Saving...</div>
          <div v-else-if="configSaved" class="config-status saved">Saved</div>
        </transition>
      </div>
    </div>

    <h3 class="section-title">Documents</h3>

    <!-- Header with search and add -->
    <div class="documents-header">
      <div class="search-box">
        <input
          v-model="filter"
          type="text"
          placeholder="Search documents..."
          class="search-input"
        />
      </div>
      <div class="header-actions">
        <div class="filter-pills">
          <button
            :class="['pill', statusFilter === '*' && 'active']"
            @click="statusFilter = '*'"
          >All</button>
          <button
            :class="['pill', statusFilter === 'active' && 'active']"
            @click="statusFilter = 'active'"
          >Active</button>
          <button
            :class="['pill', statusFilter === 'inactive' && 'active']"
            @click="statusFilter = 'inactive'"
          >Inactive</button>
        </div>
        <button class="add-btn" @click="startCreate">+ New Document</button>
      </div>
    </div>

    <p v-if="!canDrag" class="drag-hint">Clear the search/filter to drag documents into a new order.</p>

    <!-- Items List -->
    <div class="documents-list">
      <!-- New Document Form -->
      <transition name="slide-fade">
        <div v-if="isCreating && newItem" class="document-card editing">
          <div class="card-body" @click.stop>
            <input
              v-model="newItem.title"
              type="text"
              placeholder="Title (e.g. Terms of Service)..."
              class="title-input"
              ref="newTitleInput"
              @input="syncNewSlug"
            />
            <div class="slug-row">
              <span class="slug-prefix">/docs/</span>
              <input
                v-model="newItem.slug"
                type="text"
                placeholder="terms-of-service"
                class="slug-input"
                @input="newSlugTouched = true"
              />
            </div>
            <div class="rich-editor-toolbar">
              <button type="button" @click="execCommand('bold')" title="Bold"><b>B</b></button>
              <button type="button" @click="execCommand('italic')" title="Italic"><i>I</i></button>
              <button type="button" @click="execCommand('underline')" title="Underline"><u>U</u></button>
              <button type="button" @click="execCommand('insertUnorderedList')" title="Bullet List">&#9776;</button>
              <button type="button" @click="execCommand('insertOrderedList')" title="Numbered List">1.</button>
              <button type="button" @click="formatHeading" title="Heading">H</button>
              <button type="button" @click="insertLink" title="Insert Link">&#128279;</button>
              <button type="button" @click="execCommand('unlink')" title="Remove Link">&#10005;&#128279;</button>
            </div>
            <div
              ref="newBodyEditor"
              class="rich-editor"
              contenteditable="true"
              @input="newItem.body_html = $event.target.innerHTML"
              @paste="onPaste"
            ></div>
            <div class="card-footer">
              <label class="toggle-label" @click="newItem.is_active = !newItem.is_active">
                <div :class="['toggle', newItem.is_active && 'on']">
                  <div class="toggle-knob"></div>
                </div>
                <span>Active</span>
              </label>
              <div class="card-actions">
                <button class="btn-cancel" @click="cancelCreate">Cancel</button>
                <button class="btn-save" @click="saveNewItem">Create</button>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <!-- Existing Documents (draggable for reordering) -->
      <draggable
        v-model="items"
        :disabled="!canDrag"
        handle=".drag-handle"
        @end="saveOrder"
      >
        <div
          v-for="item in filteredItems"
          :key="item.id"
          :class="['document-card', editingId === item.id ? 'editing' : 'viewable']"
          @click="editingId !== item.id && startEdit(item)"
        >
          <!-- View Mode -->
          <div v-if="editingId !== item.id" class="card-row">
            <span v-if="canDrag" class="drag-handle" title="Drag to reorder" @click.stop>
              <font-awesome-icon icon="grip-vertical"/>
            </span>
            <div class="card-main">
              <div class="item-title">
                {{ item.title }}
                <span class="slug-badge">/docs/{{ item.slug }}</span>
                <span :class="['badge', item.is_active ? 'active' : 'inactive']">
                  {{ item.is_active ? 'Active' : 'Inactive' }}
                </span>
              </div>
              <div class="item-body-preview" v-html="item.body_html"></div>
            </div>
            <div class="card-actions">
              <button class="btn-delete" @click.stop="confirmDelete(item)">Delete</button>
            </div>
          </div>

          <!-- Edit Mode -->
          <div v-else class="card-body" @click.stop>
            <input
              v-model="editingItem.title"
              type="text"
              class="title-input"
              ref="editTitleInput"
            />
            <div class="slug-row">
              <span class="slug-prefix">/docs/</span>
              <input
                v-model="editingItem.slug"
                type="text"
                class="slug-input"
              />
            </div>
            <div class="rich-editor-toolbar">
              <button type="button" @click="execCommand('bold')" title="Bold"><b>B</b></button>
              <button type="button" @click="execCommand('italic')" title="Italic"><i>I</i></button>
              <button type="button" @click="execCommand('underline')" title="Underline"><u>U</u></button>
              <button type="button" @click="execCommand('insertUnorderedList')" title="Bullet List">&#9776;</button>
              <button type="button" @click="execCommand('insertOrderedList')" title="Numbered List">1.</button>
              <button type="button" @click="formatHeading" title="Heading">H</button>
              <button type="button" @click="insertLink" title="Insert Link">&#128279;</button>
              <button type="button" @click="execCommand('unlink')" title="Remove Link">&#10005;&#128279;</button>
            </div>
            <div
              ref="editBodyEditor"
              class="rich-editor rich-editor--tall"
              contenteditable="true"
              @input="editingItem.body_html = $event.target.innerHTML"
              @paste="onPaste"
            ></div>
            <div class="card-footer">
              <label class="toggle-label" @click="editingItem.is_active = !editingItem.is_active">
                <div :class="['toggle', editingItem.is_active && 'on']">
                  <div class="toggle-knob"></div>
                </div>
                <span>Active</span>
              </label>
              <div class="card-actions">
                <button class="btn-cancel" @click.stop="cancelEdit">Cancel</button>
                <button class="btn-save" @click.stop="saveEdit">Save</button>
              </div>
            </div>
          </div>
        </div>
      </draggable>

      <!-- Empty State -->
      <div v-if="!isCreating && filteredItems.length === 0" class="empty-state">
        <p v-if="filter || statusFilter !== '*'">No documents match your filters</p>
        <p v-else>No documents yet</p>
        <button class="add-btn" @click="startCreate">+ Create First Document</button>
      </div>
    </div>

    <!-- Delete Confirmation -->
    <transition name="modal-fade">
      <div v-if="deletingItem" class="modal-overlay" @click.self="deletingItem = null">
        <div class="confirm-modal">
          <h3>Delete Document</h3>
          <p>Are you sure you want to delete <strong>{{ deletingItem.title }}</strong>?</p>
          <p class="warning">This action cannot be undone.</p>
          <div class="modal-actions">
            <button class="btn-cancel" @click="deletingItem = null">Cancel</button>
            <button class="btn-delete" @click="deleteItem">Delete</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import draggable from 'vuedraggable'

export default {
  components: { draggable },

  data() {
    return {
      // Legal links config (config table key: legal_links)
      legalConfig: {
        privacy: { type: 'none', slug: '', url: '' },
        terms: { type: 'none', slug: '', url: '' }
      },
      configSaving: false,
      configSaved: false,
      configSaveTimeout: null,

      // Items
      items: [],
      filter: '',
      statusFilter: '*',

      // Create mode
      isCreating: false,
      newItem: null,
      newSlugTouched: false,

      // Edit mode
      editingId: null,
      editingItem: null,

      // Delete confirmation
      deletingItem: null
    }
  },

  computed: {
    filteredItems() {
      return this.items.filter(item => {
        if (this.statusFilter === 'active' && !item.is_active) return false
        if (this.statusFilter === 'inactive' && item.is_active) return false
        if (this.filter && !item.title.toLowerCase().includes(this.filter.toLowerCase())) return false
        return true
      })
    },

    // Dragging a filtered subset would silently rewrite the order of hidden
    // items, so reordering is only enabled on the unfiltered list.
    canDrag() {
      return !this.filter && this.statusFilter === '*' && this.editingId === null
    }
  },

  methods: {
    // Rich editor
    execCommand(command) {
      document.execCommand(command, false, null)
    },

    formatHeading() {
      document.execCommand('formatBlock', false, '<h3>')
    },

    insertLink() {
      // The prompt steals focus from the contenteditable, so save the
      // selection up front and restore it before applying the link.
      const selection = window.getSelection()
      const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null

      if (!range || range.collapsed) {
        this.$root.app.showAlert('Select the text you want to turn into a link first', 'warning')
        return
      }

      let url = window.prompt('Link URL', 'https://')
      if (!url || url === 'https://') return

      if (!/^https?:\/\//i.test(url)) {
        url = 'https://' + url
      }

      selection.removeAllRanges()
      selection.addRange(range)
      document.execCommand('createLink', false, url)
    },

    onPaste(e) {
      e.preventDefault()
      const text = e.clipboardData.getData('text/plain')
      document.execCommand('insertText', false, text)
    },

    // Legal links config
    setLegalType(which, type) {
      if (this.legalConfig[which].type === type) return
      this.legalConfig[which].type = type
      // Selecting "document"/"link" without a value yet isn't worth saving;
      // wait for the select/input change. "None" saves immediately.
      if (type === 'none') {
        this.saveLegalConfig()
      }
    },

    loadLegalConfig() {
      fetch('/admin/api/v2/config/legal_links')
        .then(resp => {
          if (resp.status === 200) return resp.json()
          if (resp.status === 404) return null // Config doesn't exist yet
          throw Error(resp.statusText)
        })
        .then(resp => {
          if (resp && resp.data && resp.data.value) {
            const v = resp.data.value
            this.legalConfig = {
              privacy: Object.assign({ type: 'none', slug: '', url: '' }, v.privacy || {}),
              terms: Object.assign({ type: 'none', slug: '', url: '' }, v.terms || {})
            }
          }
        })
        .catch(() => {
          // Silently use defaults if config doesn't exist
        })
    },

    saveLegalConfig() {
      if (this.configSaveTimeout) {
        clearTimeout(this.configSaveTimeout)
      }

      this.configSaving = true
      this.configSaved = false

      fetch('/admin/api/v2/config/legal_links', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            value: this.legalConfig,
            is_public: true
          }
        })
      })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.configSaving = false
          this.configSaved = true
          this.configSaveTimeout = setTimeout(() => {
            this.configSaved = false
          }, 2000)
        })
        .catch(() => {
          this.configSaving = false
          this.$root.app.showAlert('Error saving legal links config', 'danger')
        })
    },

    // Slug helpers
    slugify(text) {
      return (text || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    },

    syncNewSlug() {
      // Auto-derive the slug from the title until the editor touches it.
      if (!this.newSlugTouched) {
        this.newItem.slug = this.slugify(this.newItem.title)
      }
    },

    // Item methods
    emptyItem() {
      return {
        title: '',
        slug: '',
        body_html: '',
        is_active: true
      }
    },

    loadItems() {
      fetch('/admin/api/v2/documents')
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(resp => {
          this.items = resp.data || []
        })
        .catch(() => {
          this.$root.app.showAlert('Error loading documents', 'danger')
        })
    },

    // Reordering
    saveOrder() {
      const ids = this.items.map(item => item.id)

      fetch('/admin/api/v2/documents/order', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { ids: ids } })
      })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.$root.app.showAlert('Order saved', 'success')
        })
        .catch(() => {
          this.$root.app.showAlert('Error saving order', 'danger')
          this.loadItems()
        })
    },

    validateItem(item) {
      if (!item.title.trim()) {
        this.$root.app.showAlert('Please enter a title', 'warning')
        return false
      }
      if (!/^[a-z0-9-]+$/.test(item.slug)) {
        this.$root.app.showAlert('Slug may contain only lowercase letters, numbers and dashes', 'warning')
        return false
      }
      return true
    },

    // Create
    startCreate() {
      this.cancelEdit()
      this.isCreating = true
      this.newItem = this.emptyItem()
      this.newSlugTouched = false
      this.$nextTick(() => {
        if (this.$refs.newBodyEditor) this.$refs.newBodyEditor.innerHTML = ''
        if (this.$refs.newTitleInput) this.$refs.newTitleInput.focus()
      })
    },

    cancelCreate() {
      this.isCreating = false
      this.newItem = this.emptyItem()
    },

    saveNewItem() {
      if (!this.validateItem(this.newItem)) return

      fetch('/admin/api/v2/documents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: this.newItem })
      })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.$root.app.showAlert('Document created successfully', 'success')
          this.cancelCreate()
          this.loadItems()
        })
        .catch(() => {
          this.$root.app.showAlert('Error creating document (is the slug unique?)', 'danger')
        })
    },

    // Edit
    startEdit(item) {
      this.cancelCreate()
      this.editingId = item.id
      this.editingItem = { ...item }
      this.$nextTick(() => {
        // editBodyEditor lives inside a v-for, so the ref is an array.
        const editor = Array.isArray(this.$refs.editBodyEditor)
          ? this.$refs.editBodyEditor[0]
          : this.$refs.editBodyEditor
        if (editor) editor.innerHTML = this.editingItem.body_html || ''
        const input = Array.isArray(this.$refs.editTitleInput)
          ? this.$refs.editTitleInput[0]
          : this.$refs.editTitleInput
        if (input) input.focus()
      })
    },

    cancelEdit() {
      this.editingId = null
      this.editingItem = null
    },

    saveEdit() {
      if (!this.validateItem(this.editingItem)) return

      fetch(`/admin/api/v2/documents/${this.editingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: this.editingItem })
      })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.$root.app.showAlert('Document updated successfully', 'success')
          this.cancelEdit()
          this.loadItems()
        })
        .catch(() => {
          this.$root.app.showAlert('Error updating document (is the slug unique?)', 'danger')
        })
    },

    // Delete
    confirmDelete(item) {
      this.deletingItem = item
    },

    deleteItem() {
      fetch(`/admin/api/v2/documents/${this.deletingItem.id}`, { method: 'DELETE' })
        .then(resp => {
          if (resp.status === 200) return resp.json()
          throw Error(resp.statusText)
        })
        .then(() => {
          this.$root.app.showAlert('Document deleted successfully', 'success')
          this.deletingItem = null
          this.loadItems()
        })
        .catch(() => {
          this.$root.app.showAlert('Error deleting document', 'danger')
        })
    }
  },

  mounted() {
    this.$root.app.setActivePageTitle('Documents', false)
    this.$root.$emit('set-toolbar', 'empty-toolbar')
    this.newItem = this.emptyItem()
    this.loadLegalConfig()
    this.loadItems()
  }
}
</script>

<style scoped>
.documents-container {
  padding: 1.5rem;
  max-width: 1000px;
  margin: 0 auto;
}

/* Section titles */
.section-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
}

/* Config Section */
.config-section {
  margin-bottom: 2rem;
}

.config-card {
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  padding: 1.5rem;
  position: relative;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.config-row {
  display: flex;
  gap: 3rem;
  flex-wrap: wrap;
}

.config-item {
  min-width: 280px;
}

.config-label {
  font-size: 0.85rem;
  color: #E2E8F0;
  margin-bottom: 0.5rem;
  display: block;
  font-weight: 600;
}

.config-hint {
  font-size: 0.75rem;
  color: #94A3B8;
  margin-top: 0.4rem;
}

.type-toggles {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.type-chip {
  padding: 0.35rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: #94A3B8;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.2s;
  user-select: none;
}

.type-chip:hover {
  border-color: #20B4FF;
  color: #fff;
  background: rgba(255, 255, 255, 0.1);
}

.type-chip.active {
  background: linear-gradient(135deg, #20B4FF 0%, #007BFF 100%);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 2px 4px rgba(32, 180, 255, 0.2);
}

.legal-select,
.legal-url-input {
  width: 100%;
  padding: 0.45rem 0.6rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 0.85rem;
}

.legal-select:focus,
.legal-url-input:focus {
  outline: none;
  border-color: #20B4FF;
}

.legal-select option {
  background: #2a2e35;
  color: #fff;
}

.config-status {
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.config-status.saving {
  color: #9ca3af;
}

.config-status.saved {
  color: #28a745;
  background: #28a74520;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Header */
.documents-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;
  flex-wrap: wrap;
}

.search-box {
  flex: 1;
  max-width: 280px;
}

.search-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #20B4FF;
  background: rgba(255, 255, 255, 0.1);
  box-shadow: 0 0 0 2px rgba(32, 180, 255, 0.2);
}

.search-input::placeholder {
  color: #94A3B8;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.filter-pills {
  display: flex;
  gap: 4px;
  background: rgba(255, 255, 255, 0.03);
  padding: 4px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.pill {
  padding: 0.35rem 0.75rem;
  border: none;
  background: transparent;
  color: #94A3B8;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.2s;
}

.pill:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.05);
}

.pill.active {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.add-btn {
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #20B4FF 0%, #007BFF 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9rem;
  transition: all 0.2s;
  box-shadow: 0 4px 6px rgba(32, 180, 255, 0.2);
}

.add-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 12px rgba(32, 180, 255, 0.3);
}

.drag-hint {
  font-size: 0.75rem;
  color: #94A3B8;
  margin-bottom: 0.75rem;
}

/* List */
.documents-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Card */
.document-card {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  margin-bottom: 0.75rem;
  overflow: hidden;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.document-card.viewable {
  cursor: pointer;
}

.document-card.viewable:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
}

.document-card.editing {
  background: rgba(255, 255, 255, 0.08);
  border-color: #20B4FF;
  box-shadow: 0 0 0 1px #20B4FF, 0 10px 25px rgba(0, 0, 0, 0.2);
}

.card-row {
  display: flex;
  padding: 1rem 1.25rem;
  align-items: flex-start;
  gap: 1rem;
}

.card-body {
  padding: 1rem 1.25rem;
}

.drag-handle {
  color: #94A3B8;
  cursor: grab;
  padding-top: 0.15rem;
}

.drag-handle:active {
  cursor: grabbing;
}

/* Card Main */
.card-main {
  flex: 1;
  min-width: 0;
}

.item-title {
  font-weight: 600;
  font-size: 1rem;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.item-body-preview {
  font-size: 0.85rem;
  color: #94A3B8;
  margin-top: 0.35rem;
  max-height: 3.6em;
  overflow: hidden;
}

.slug-badge {
  font-size: 0.75rem;
  font-family: monospace;
  color: #94A3B8;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
}

.badge {
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
}

.badge.active {
  background: rgba(40, 167, 69, 0.15);
  color: #28a745;
  border: 1px solid rgba(40, 167, 69, 0.2);
}

.badge.inactive {
  background: rgba(108, 117, 125, 0.15);
  color: #94A3B8;
  border: 1px solid rgba(108, 117, 125, 0.2);
}

.title-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

.title-input:focus {
  outline: none;
  border-color: #20B4FF;
}

.slug-row {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.75rem;
}

.slug-prefix {
  color: #94A3B8;
  font-family: monospace;
  font-size: 0.85rem;
}

.slug-input {
  flex: 1;
  padding: 0.4rem 0.6rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-family: monospace;
  font-size: 0.85rem;
}

.slug-input:focus {
  outline: none;
  border-color: #20B4FF;
}

/* Rich editor */
.rich-editor-toolbar {
  display: flex;
  gap: 0.25rem;
  margin-bottom: 0.35rem;
  flex-wrap: wrap;
}

.rich-editor-toolbar button {
  min-width: 2rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.05);
  color: #E2E8F0;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.15s;
}

.rich-editor-toolbar button:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: #20B4FF;
}

.rich-editor {
  min-height: 9rem;
  max-height: 24rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 0.9rem;
  line-height: 1.5;
  overflow-y: auto;
}

.rich-editor--tall {
  min-height: 14rem;
}

.rich-editor:focus {
  outline: none;
  border-color: #20B4FF;
}

/* Links are runtime content (v-html / contenteditable), so scoped styles need
   deep selectors to reach them. */
.rich-editor >>> a,
.item-body-preview >>> a {
  color: #20B4FF;
  text-decoration: underline;
}

/* Toggles */
.toggle-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
}

.toggle-label span {
  font-size: 0.85rem;
  color: #E2E8F0;
}

.toggle {
  width: 36px;
  height: 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  position: relative;
  transition: all 0.2s;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.toggle.on {
  background: #20B4FF;
  border-color: #20B4FF;
}

.toggle-knob {
  width: 16px;
  height: 16px;
  background: #fff;
  border-radius: 50%;
  position: absolute;
  top: 1px;
  left: 1px;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0,0,0,0.2);
}

.toggle.on .toggle-knob {
  left: 17px;
}

/* Footer of edit forms */
.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.75rem;
}

/* Actions */
.card-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.btn-save, .btn-cancel, .btn-delete {
  padding: 0.4rem 0.75rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.15s;
}

.btn-save {
  background: #28a745;
  color: #fff;
}

.btn-save:hover {
  background: #218838;
}

.btn-cancel {
  background: #3a3f47;
  color: #c9d1d9;
}

.btn-cancel:hover {
  background: #4a5058;
  color: #fff;
}

.btn-delete {
  background: transparent;
  color: #adb5bd;
}

.btn-delete:hover {
  background: #dc3545;
  color: #fff;
}

/* Empty state */
.empty-state {
  text-align: center;
  padding: 3rem;
  color: #9ca3af;
}

.empty-state p {
  margin-bottom: 1rem;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.confirm-modal {
  background: #2a2e35;
  border-radius: 8px;
  padding: 1.5rem;
  max-width: 360px;
  width: 90%;
  border: 1px solid #3a3f47;
}

.confirm-modal h3 {
  margin: 0 0 0.75rem;
  color: #fff;
  font-size: 1.1rem;
}

.confirm-modal p {
  color: #c9d1d9;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.confirm-modal .warning {
  color: #dc3545;
  font-size: 0.8rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.25rem;
}

/* Transitions */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.15s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .documents-header {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    max-width: none;
  }

  .header-actions {
    justify-content: space-between;
  }
}
</style>
